import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';

import {
    getPortfolio,
    toggleShare,
    revokePortfolioBadge,
    getPortfolioEndorsements,
    getPortfolioEvidence,
    addEvidence,
    deleteEvidence,
    requestEndorsementForBadge,
    PortfolioItem,
    PortfolioEndorsement,
    BadgeEvidence,
} from '../api/portfolio';
import { PURPLE_AVATAR } from '../theme/tokens';
import StackedCard from '../components/StackedCard';
import { isMultiBadge } from '../constants/badgeTypes';

const PAGE_SIZE = 12;

function formatDate(iso: string | null | undefined) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Badge card — matches BadgeCard.tsx style ───────────────────────────────

function BadgeCard({ item, onClick }: { item: PortfolioItem; onClick: () => void }) {
    return (
        <StackedCard enabled={isMultiBadge(item.badgeType)}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Purple image header — identical to BadgeCard.tsx */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor={PURPLE_AVATAR}
                sx={{ p: 2, aspectRatio: '1', width: '100%' }}
            >
                {item.badgeImage ? (
                    <Box
                        component="img"
                        src={item.badgeImage}
                        alt={item.badgeName}
                        sx={{ width: 250, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                    />
                ) : (
                    <WorkspacePremiumIcon sx={{ fontSize: 56, color: 'primary.main' }} />
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom noWrap>{item.badgeName}</Typography>
                {item.issuerName && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 14 }} />{item.issuerName}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" mb={1}>
                    Earned {formatDate(item.issuedDate)}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                    <Chip
                        label={item.isPublic ? 'Public' : 'Private'}
                        color={item.isPublic ? 'success' : 'default'}
                        size="small"
                    />
                    {item.isSharedToProfile && (
                        <Chip label="Shared" size="small" color="primary" />
                    )}
                    {item.endorsementCount > 0 && (
                        <Chip label={`${item.endorsementCount} endorsement${item.endorsementCount !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    )}
                    {item.evidenceCount > 0 && (
                        <Chip label={`${item.evidenceCount} evidence`} size="small" variant="outlined" />
                    )}
                </Box>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2 }}>
                <Button variant="contained" size="small" onClick={onClick}>
                    View Details
                </Button>
            </CardActions>
        </Card>
        </StackedCard>
    );
}

// ── Evidence tab ───────────────────────────────────────────────────────────

function EvidenceTab({ issuedBadgeID }: { issuedBadgeID: string }) {
    const [items, setItems] = useState<BadgeEvidence[]>([]);
    const [loading, setLoading] = useState(true);
    const [addUrl, setAddUrl] = useState('');
    const [addText, setAddText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        const res = await getPortfolioEvidence(issuedBadgeID);
        setItems(res.success && res.data ? res.data.evidences : []);
        setLoading(false);
    }, [issuedBadgeID]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        if (!addUrl.trim() && !addText.trim()) {
            setError('Enter a URL or text to add evidence.');
            return;
        }
        setSaving(true);
        setError('');
        setSuccess('');
        const res = await addEvidence({
            openBadgeIssueID: issuedBadgeID,
            evidenceURL: addUrl.trim() || undefined,
            evidenceText: addText.trim() || undefined,
        });
        setSaving(false);
        if (res.success) {
            setAddUrl('');
            setAddText('');
            setSuccess('Evidence saved.');
            load();
        } else {
            setError(res.message ?? 'Failed to save evidence.');
        }
    };

    const handleDelete = async (id: string, key: 'url' | 'text') => {
        const res = await deleteEvidence(id, key);
        if (res.success) load();
    };

    if (loading) return <Box py={3} textAlign="center"><CircularProgress size={32} /></Box>;

    return (
        <Box>
            {items.length === 0 && (
                <Typography color="text.secondary" mb={2}>No evidence added yet.</Typography>
            )}
            {items.map(ev => (
                <Box key={ev.ID} mb={1.5}>
                    {ev.evidenceURL && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <LinkIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                                <a href={ev.evidenceURL} target="_blank" rel="noopener noreferrer">{ev.evidenceURL}</a>
                            </Typography>
                            <IconButton size="small" onClick={() => handleDelete(ev.ID, 'url')} title="Remove URL">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                    {ev.evidenceText && (
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            <TextFieldsIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                            <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: 'pre-wrap' }}>
                                {ev.evidenceText}
                            </Typography>
                            <IconButton size="small" onClick={() => handleDelete(ev.ID, 'text')} title="Remove text">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                    <Divider sx={{ mt: 1 }} />
                </Box>
            ))}

            <Box mt={2}>
                <Typography variant="subtitle2" mb={1}>Add Evidence</Typography>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
                <TextField
                    label="Evidence URL"
                    fullWidth
                    size="small"
                    value={addUrl}
                    onChange={e => setAddUrl(e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder="https://example.com/my-work"
                />
                <TextField
                    label="Evidence Text"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={addText}
                    onChange={e => setAddText(e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder="Describe what you did to earn this badge…"
                />
                <Button variant="contained" size="small" onClick={handleAdd} disabled={saving}>
                    {saving ? 'Saving…' : 'Add Evidence'}
                </Button>
            </Box>
        </Box>
    );
}

// ── Endorsements tab ───────────────────────────────────────────────────────

function EndorsementsTab({ badgeID }: { badgeID: string }) {
    const [items, setItems] = useState<PortfolioEndorsement[]>([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [requesting, setRequesting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        getPortfolioEndorsements(badgeID).then(res => {
            setItems(res.success && res.data ? res.data : []);
            setLoading(false);
        });
    }, [badgeID]);

    const handleRequest = async () => {
        if (!email.trim()) {
            setError('Please enter the endorser\'s email address.');
            return;
        }
        setRequesting(true);
        setError('');
        setSuccess('');
        const res = await requestEndorsementForBadge({
            badgeID,
            endorserEmail: email.trim(),
            requestMessage: message.trim() || undefined,
        });
        setRequesting(false);
        if (res.success) {
            setEmail('');
            setMessage('');
            setSuccess('Endorsement request sent!');
        } else {
            setError(res.message ?? 'Failed to send request.');
        }
    };

    if (loading) return <Box py={3} textAlign="center"><CircularProgress size={32} /></Box>;

    return (
        <Box>
            {items.length === 0 && (
                <Typography color="text.secondary" mb={2}>No approved endorsements yet.</Typography>
            )}
            {items.map(e => (
                <Box key={e.endorsementID} mb={2}>
                    <Typography variant="body2" fontWeight={600}>
                        {e.endorserName}
                        {e.endorserOrganization ? ` — ${e.endorserOrganization}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                        {formatDate(e.dateCreated)}
                    </Typography>
                    <Typography variant="body2">{e.endorsementText}</Typography>
                    <Divider sx={{ mt: 1.5 }} />
                </Box>
            ))}

            <Box mt={2}>
                <Typography variant="subtitle2" mb={1}>Request an Endorsement</Typography>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
                <TextField
                    label="Endorser Email"
                    fullWidth
                    size="small"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder="colleague@example.com"
                />
                <TextField
                    label="Personal Message (optional)"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder="Hi, I'd appreciate your endorsement of this badge…"
                />
                <Button variant="contained" size="small" onClick={handleRequest} disabled={requesting}>
                    {requesting ? 'Sending…' : 'Send Request'}
                </Button>
            </Box>
        </Box>
    );
}

// ── Detail dialog ──────────────────────────────────────────────────────────

function BadgeDialog({
    item,
    onClose,
    onShareToggled,
    onRevoked,
}: {
    item: PortfolioItem;
    onClose: () => void;
    onShareToggled: (issuedBadgeID: string, newValue: boolean) => void;
    onRevoked: (issuedBadgeID: string) => void;
}) {
    const [tab, setTab] = useState(0);
    const [toggling, setToggling] = useState(false);
    const [shared, setShared] = useState(item.isSharedToProfile);
    const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [revokeError, setRevokeError] = useState('');

    const shareUrl = `${window.location.origin}/badges/${item.badgeID}`;
    const text = encodeURIComponent(`I earned the "${item.badgeName}" badge!`);
    const url = encodeURIComponent(shareUrl);

    const handleToggle = async () => {
        setToggling(true);
        const res = await toggleShare(item.issuedBadgeID);
        setToggling(false);
        if (res.success && res.data) {
            setShared(res.data.isSharedToProfile);
            onShareToggled(item.issuedBadgeID, res.data.isSharedToProfile);
        }
    };

    const handleRevoke = async () => {
        setRevoking(true);
        setRevokeError('');
        const res = await revokePortfolioBadge(item.issuedBadgeID);
        setRevoking(false);
        if (res.success) {
            setConfirmRevokeOpen(false);
            onRevoked(item.issuedBadgeID);
        } else {
            setRevokeError(res.message ?? 'Failed to remove badge.');
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ pr: 6 }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    {item.badgeImage ? (
                        <Box
                            component="img"
                            src={item.badgeImage}
                            alt={item.badgeName}
                            sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, flexShrink: 0 }}
                        />
                    ) : (
                        <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'accent.main', flexShrink: 0 }} />
                    )}
                    <Box minWidth={0}>
                        <Typography fontWeight={700} noWrap>{item.badgeName}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>{item.issuerName}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Details" />
                <Tab label="Evidence" />
                <Tab label="Endorsements" />
            </Tabs>

            <DialogContent dividers={false} sx={{ pt: 2 }}>
                {tab === 0 && (
                    <Box>
                        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                            <Box>
                                <Typography variant="caption" color="text.secondary">Earned</Typography>
                                <Typography variant="body2">{formatDate(item.issuedDate)}</Typography>
                            </Box>
                            {item.expiresOn && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Expires</Typography>
                                    <Typography variant="body2">{formatDate(item.expiresOn)}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" color="text.secondary">Visibility</Typography>
                                <Typography variant="body2">{item.isPublic ? 'Public' : 'Private'}</Typography>
                            </Box>
                        </Box>

                        {item.badgeDescription && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" mb={0.5}>Description</Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    dangerouslySetInnerHTML={{ __html: item.badgeDescription }}
                                />
                            </Box>
                        )}

                        {item.badgeCriteria && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" mb={0.5}>Criteria</Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    dangerouslySetInnerHTML={{ __html: item.badgeCriteria }}
                                />
                            </Box>
                        )}

                        {item.tags.length > 0 && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" mb={0.5}>Tags</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {item.tags.map(t => <Chip key={t} label={t} size="small" />)}
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Public profile share */}
                        <Box mb={2}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shared}
                                        onChange={handleToggle}
                                        disabled={toggling}
                                        size="small"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>Share to public profile</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Make this badge visible on your public profile page
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>

                        {/* Social sharing */}
                        <Box>
                            <Typography variant="subtitle2" mb={1}>Share on social media</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    component="a"
                                    href={`https://twitter.com/intent/tweet?text=${text}&url=${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ borderColor: 'text.primary', color: 'text.primary', textTransform: 'none' }}
                                >
                                    𝕏 Post
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    component="a"
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ borderColor: '#0A66C2', color: '#0A66C2', textTransform: 'none' }}
                                >
                                    LinkedIn
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    component="a"
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ borderColor: '#1877F2', color: '#1877F2', textTransform: 'none' }}
                                >
                                    Facebook
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                {tab === 1 && <EvidenceTab issuedBadgeID={item.issuedBadgeID} />}
                {tab === 2 && <EndorsementsTab badgeID={item.badgeID} />}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                    onClick={() => { setRevokeError(''); setConfirmRevokeOpen(true); }}
                    color="error"
                    startIcon={<DeleteIcon />}
                >
                    Remove from portfolio
                </Button>
                <Button color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>

            <Dialog open={confirmRevokeOpen} onClose={() => !revoking && setConfirmRevokeOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Remove badge?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Remove "{item.badgeName}" from your portfolio? You can earn this badge again afterward.
                    </Typography>
                    {revokeError && <Alert severity="error" sx={{ mt: 2 }}>{revokeError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setConfirmRevokeOpen(false)} disabled={revoking}>Cancel</Button>
                    <Button onClick={handleRevoke} color="error" disabled={revoking}>
                        {revoking ? 'Removing…' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function PortfolioPage() {
    const [items, setItems] = useState<PortfolioItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<'issuedDate' | 'name'>('issuedDate');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<PortfolioItem | null>(null);
    const [revokedSnack, setRevokedSnack] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const autoOpenBadgeID = searchParams.get('badgeID');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        const res = await getPortfolio({ page, pageSize: PAGE_SIZE, search: search || undefined, sort, order });
        if (res.success && res.data) {
            setItems(res.data);
            setTotal(res.total ?? 0);
        } else {
            setError(res.message ?? 'Failed to load portfolio.');
        }
        setLoading(false);
    }, [page, search, sort, order]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!autoOpenBadgeID || items.length === 0) return;
        const match = items.find(i => i.badgeID.toLowerCase() === autoOpenBadgeID.toLowerCase());
        if (match) setSelected(match);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('badgeID');
            return next;
        }, { replace: true });
    }, [autoOpenBadgeID, items, setSearchParams]);

    const handleSearch = () => {
        setPage(1);
        setSearch(searchInput.trim());
    };

    const handleShareToggled = (issuedBadgeID: string, newValue: boolean) => {
        setItems(prev => prev.map(i => i.issuedBadgeID === issuedBadgeID ? { ...i, isSharedToProfile: newValue } : i));
        if (selected?.issuedBadgeID === issuedBadgeID) {
            setSelected(prev => prev ? { ...prev, isSharedToProfile: newValue } : prev);
        }
    };

    const handleRevoked = (issuedBadgeID: string) => {
        const removed = items.find(i => i.issuedBadgeID === issuedBadgeID);
        setSelected(null);
        setRevokedSnack(removed ? `Removed "${removed.badgeName}" from your portfolio.` : 'Badge removed from your portfolio.');
        load();
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>My Portfolio</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total > 0 ? `${total} badge${total !== 1 ? 's' : ''} earned` : 'Your earned badges will appear here'}
                    </Typography>
                </Box>

                {/* Controls */}
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search badges…"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 220 }}
                    />
                    <Button onClick={handleSearch}>Search</Button>
                    <Select
                        size="small"
                        value={`${sort}-${order}`}
                        onChange={e => {
                            const [s, o] = e.target.value.split('-');
                            setSort(s as 'issuedDate' | 'name');
                            setOrder(o as 'asc' | 'desc');
                            setPage(1);
                        }}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="issuedDate-desc">Newest First</MenuItem>
                        <MenuItem value="issuedDate-asc">Oldest First</MenuItem>
                        <MenuItem value="name-asc">Name A–Z</MenuItem>
                        <MenuItem value="name-desc">Name Z–A</MenuItem>
                    </Select>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
                <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 1 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : items.length === 0 ? (
                <Box textAlign="center" py={8}>
                    <WorkspacePremiumIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        {search ? 'No badges match your search.' : 'You haven\'t earned any badges yet.'}
                    </Typography>
                    {!search && (
                        <Typography variant="body2" color="text.disabled" mt={1}>
                            Complete activities and courses to earn badges.
                        </Typography>
                    )}
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {items.map(item => (
                        <Grid item xs={12} sm={6} md={4} key={item.issuedBadgeID}>
                            <BadgeCard item={item} onClick={() => setSelected(item)} />
                        </Grid>
                    ))}
                </Grid>
            )}

            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                    />
                </Box>
            )}

            {selected && (
                <BadgeDialog
                    item={selected}
                    onClose={() => setSelected(null)}
                    onShareToggled={handleShareToggled}
                    onRevoked={handleRevoked}
                />
            )}

            <Snackbar
                open={!!revokedSnack}
                autoHideDuration={4000}
                onClose={() => setRevokedSnack('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setRevokedSnack('')} sx={{ width: '100%' }}>
                    {revokedSnack}
                </Alert>
            </Snackbar>
        </Container>
    );
}
